export interface Triangle {
  a: number;
  b: number;
  c: number;
  A: number;
  B: number;
  C: number;
}

export type SolveResult =
  | { unique: Triangle }
  | { ambiguous: [Triangle, Triangle] }
  | { invalid: 'underdetermined' | 'inconsistent' };

const EPS = 1e-9;

function clampToUnit(v: number): number {
  return Math.min(1, Math.max(-1, v));
}

function countDefined<T extends object>(obj: Partial<T>, keys: ReadonlyArray<keyof T>): number {
  return keys.filter((k) => obj[k] !== undefined).length;
}

function finish(
  angles: Partial<Pick<Triangle, 'A' | 'B' | 'C'>>,
): Pick<Triangle, 'A' | 'B' | 'C'> | null {
  const known = (Object.values(angles) as Array<number | undefined>).filter(
    (v): v is number => typeof v === 'number',
  );
  if (known.length === 2) {
    const sum = known.reduce((s, v) => s + v, 0);
    const missing = Math.PI - sum;
    if (missing <= EPS) return null;
    const completed: Pick<Triangle, 'A' | 'B' | 'C'> = {
      A: angles.A ?? 0,
      B: angles.B ?? 0,
      C: angles.C ?? 0,
    };
    if (angles.A === undefined) completed.A = missing;
    else if (angles.B === undefined) completed.B = missing;
    else completed.C = missing;
    return completed;
  }
  if (known.length === 3) {
    const sum = (angles.A ?? 0) + (angles.B ?? 0) + (angles.C ?? 0);
    if (Math.abs(sum - Math.PI) > EPS) return null;
    return { A: angles.A!, B: angles.B!, C: angles.C! };
  }
  return null;
}

export function solveTriangle(given: Partial<Triangle>): SolveResult {
  const sides = countDefined(given, ['a', 'b', 'c'] as const);
  const angles = countDefined(given, ['A', 'B', 'C'] as const);

  if (sides === 0) return { invalid: 'underdetermined' };
  if (sides + angles < 3) return { invalid: 'underdetermined' };

  // SSS
  if (sides === 3) {
    const { a, b, c } = given as { a: number; b: number; c: number };
    if (a + b <= c + EPS || b + c <= a + EPS || a + c <= b + EPS) {
      return { invalid: 'inconsistent' };
    }
    const A = Math.acos(clampToUnit((b * b + c * c - a * a) / (2 * b * c)));
    const B = Math.acos(clampToUnit((a * a + c * c - b * b) / (2 * a * c)));
    const C = Math.PI - A - B;
    return { unique: { a, b, c, A, B, C } };
  }

  // SAS (any side, included angle, another side)
  for (const [s1, ang, s2, opp] of [
    ['a', 'C', 'b', 'c'],
    ['b', 'A', 'c', 'a'],
    ['a', 'B', 'c', 'b'],
  ] as const) {
    if (
      given[s1] !== undefined &&
      given[ang] !== undefined &&
      given[s2] !== undefined &&
      sides === 2 &&
      angles === 1
    ) {
      const x = given[s1];
      const y = given[s2];
      const Theta = given[ang];
      if (Theta <= 0 || Theta >= Math.PI) return { invalid: 'inconsistent' };
      const oppLen = Math.sqrt(x * x + y * y - 2 * x * y * Math.cos(Theta));
      const next: Partial<Triangle> = { ...given, [opp]: oppLen };
      return solveTriangle(next);
    }
  }

  // ASA / AAS (2 angles + 1 side)
  if (angles >= 2 && sides >= 1) {
    const anglesArg: Partial<Pick<Triangle, 'A' | 'B' | 'C'>> = {};
    if (given.A !== undefined) anglesArg.A = given.A;
    if (given.B !== undefined) anglesArg.B = given.B;
    if (given.C !== undefined) anglesArg.C = given.C;
    const completedAngles = finish(anglesArg);
    if (!completedAngles) return { invalid: 'inconsistent' };
    const { A, B, C } = completedAngles;
    const sideKeys: Array<'a' | 'b' | 'c'> = ['a', 'b', 'c'];
    const known = sideKeys.find((k) => given[k] !== undefined)!;
    const knownAngle = known === 'a' ? A : known === 'b' ? B : C;
    const ratio = given[known]! / Math.sin(knownAngle);
    return {
      unique: {
        a: ratio * Math.sin(A),
        b: ratio * Math.sin(B),
        c: ratio * Math.sin(C),
        A,
        B,
        C,
      },
    };
  }

  // SSA — the ambiguous case
  // Given: an angle and the side opposite it, plus another side
  for (const [oppSide, oppAngle, otherSide, otherAngle] of [
    ['a', 'A', 'b', 'B'],
    ['a', 'A', 'c', 'C'],
    ['b', 'B', 'a', 'A'],
    ['b', 'B', 'c', 'C'],
    ['c', 'C', 'a', 'A'],
    ['c', 'C', 'b', 'B'],
  ] as const) {
    if (
      given[oppSide] !== undefined &&
      given[oppAngle] !== undefined &&
      given[otherSide] !== undefined &&
      given[otherAngle] === undefined
    ) {
      const a = given[oppSide];
      const b = given[otherSide];
      const A = given[oppAngle];
      const sinB = (b * Math.sin(A)) / a;
      if (sinB > 1 + EPS) return { invalid: 'inconsistent' };
      const clamped = clampToUnit(sinB);
      const B1 = Math.asin(clamped);
      const B2 = Math.PI - B1;
      const buildFrom = (B: number): Triangle | null => {
        const C = Math.PI - A - B;
        if (C <= EPS) return null;
        const c = (a * Math.sin(C)) / Math.sin(A);
        const out: Partial<Triangle> = {
          [oppSide]: a,
          [otherSide]: b,
          [oppAngle]: A,
          [otherAngle]: B,
        };
        // Identify the remaining side/angle keys
        const allSides: Array<'a' | 'b' | 'c'> = ['a', 'b', 'c'];
        const allAngles: Array<'A' | 'B' | 'C'> = ['A', 'B', 'C'];
        const thirdSide = allSides.find((k) => k !== oppSide && k !== otherSide)!;
        const thirdAngle = allAngles.find((k) => k !== oppAngle && k !== otherAngle)!;
        out[thirdSide] = c;
        out[thirdAngle] = C;
        return out as Triangle;
      };
      const t1 = buildFrom(B1);
      const t2 = buildFrom(B2);
      if (!t1 && !t2) return { invalid: 'inconsistent' };
      if (t1 && t2) return { ambiguous: [t1, t2] };
      return { unique: (t1 ?? t2)! };
    }
  }

  return { invalid: 'underdetermined' };
}

export function triangleArea(t: Triangle): number {
  return 0.5 * t.a * t.b * Math.sin(t.C);
}
